// Types
import type { Ref } from 'vue';
import type { TListingFilters, TStoredFilters, TWrappedFilters } from '~/misc/listings/types';
import type { TManagerCompanyFiltersResponse } from '~/modules/manager/types';
import type { TEntityStatus } from '~/misc/opportunity/types';
import type { IGlobalFiltersStateOptions, IGlobalFiltersState } from './types';

// Config
import { DEFAULT_LISTINGS_FILTERS, ALL_MARKETING_CONSENT } from '~/misc/listings/filters/config';

// Services
import { ManagerOpportunityDataClient } from '~/modules/manager/services/manager-opportunity-data-client';

// Composables
import { useFilterTimestamps } from '~/misc/composables/use-filter-timestamps';

// Models
import { ManagerCompanyModel } from '~/modules/manager/models/company.model';

// Utils
import { extractStoredFilters } from '~/misc/listings/utils/filter-extractor';
import { cleanStoredFilters } from '~/misc/listings/utils/filter-cleaner';

/**
 * Manages the lifecycle of manager global filters for a company patch.
 *
 * Handles loading filters from the API, tracking per-field changes with
 * timestamps, and saving only modified fields back via PATCH. The full
 * GET response is merged with changed fields before sending so the
 * backend always receives a complete filter payload.
 *
 * @param options.companyId   - Reactive company ID (skips load/save when null).
 * @param options.selectedType - Current sale type (S or L).
 * @param options.selectedPatchId - Current patch ID (skips load when undefined).
 * @param options.onStateSnapshot - Called after load and save to capture state.
 *
 * @returns filters            - Reactive listing filters bound to the UI.
 * @returns isLoading          - True while the GET request is in flight.
 * @returns isSaving           - True while the PATCH request is in flight.
 * @returns hasError           - True when the last load or save failed.
 * @returns load               - Fetches filters from the API and applies them.
 * @returns save               - Wraps current filters and PATCHes the API.
 * @returns reset              - Resets filters to defaults with current sale type.
 * @returns extractStoredFilters - Returns the current filters in stored format.
 * @returns applyStoredFilters - Applies a raw API response to the reactive state.
 */
export const useGlobalFiltersState = (options: IGlobalFiltersStateOptions): IGlobalFiltersState => {
  const { companyId, selectedType, selectedPatchId, isExternalCompany, onStateSnapshot } = options;

  const managerCompanyModel: ManagerCompanyModel = new ManagerCompanyModel();

  const entityStatuses: Ref<TEntityStatus[]> = ref<TEntityStatus[]>([]);

  const getAllEntityStatusIds = (): number[] => entityStatuses.value.map((s: TEntityStatus) => s.id);

  const loadEntityStatuses = async (): Promise<void> => {
    if (entityStatuses.value.length > 0) return;

    try {
      const response: TEntityStatus[] = await new ManagerOpportunityDataClient().request<
        TEntityStatus[]
      >('entityStatuses');
      entityStatuses.value = response;
    } catch {
      // Non-critical: the dropdown will retry on open
    }
  };

  const getDefaultFilters = (): TListingFilters => ({
    ...DEFAULT_LISTINGS_FILTERS,
    exclude_archived: 0,
    marketing_consent: isExternalCompany ? [...ALL_MARKETING_CONSENT] : [],
    entity_status: getAllEntityStatusIds(),
  });

  const filters: Ref<TListingFilters> = ref<TListingFilters>({ ...getDefaultFilters() });
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const isSaving: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);

  const { unwrapResponse, wrapForSave, syncSaved, updateBaseline } = useFilterTimestamps();

  const setFilters = (newFilters: TListingFilters): void => {
    filters.value = newFilters;
  };

  const setIsLoading = (loading: boolean): void => {
    isLoading.value = loading;
  };

  const setIsSaving = (saving: boolean): void => {
    isSaving.value = saving;
  };

  const setHasError = (error: boolean): void => {
    hasError.value = error;
  };

  const getStoredFilters = (): TStoredFilters => extractStoredFilters(filters.value);

  const applyStoredFilters = (response: TManagerCompanyFiltersResponse): void => {
    // Remove sale_type — it's not part of the wrapped filter fields
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { sale_type: saleType, ...wrappedFields } = response;

    const { filters: rawFilters } = unwrapResponse(wrappedFields);

    const mergedFilters: TListingFilters = {
      ...getDefaultFilters(),
      ...cleanStoredFilters(rawFilters),
      type: selectedType.value,
    };

    // Normalize empty entity_status to "all selected" — the API treats [] as "no filter"
    if (mergedFilters.entity_status.length === 0) {
      mergedFilters.entity_status = getAllEntityStatusIds();
    }

    setFilters(mergedFilters);

    // Align baseline with the normalized filter state so fields
    // missing from the API response (e.g. price_range) are not
    // treated as changed on the next save.
    updateBaseline(getStoredFilters());
  };

  const reset = (): void => {
    setFilters({ ...getDefaultFilters(), type: selectedType.value });
  };

  const load = async (): Promise<void> => {
    if (!companyId.value || !selectedPatchId.value) return;

    setIsLoading(true);
    setHasError(false);

    try {
      // Load entity statuses and filters in parallel
      const [response] = await Promise.all([
        managerCompanyModel
          .getFilters(companyId.value, {
            sale_type: selectedType.value,
            patch_id: selectedPatchId.value,
          })
          .catch(() => undefined) as Promise<TManagerCompanyFiltersResponse | undefined>,
        loadEntityStatuses(),
      ]);

      if (response && !Array.isArray(response)) {
        applyStoredFilters(response);
      } else {
        reset();
      }

      onStateSnapshot();
    } catch {
      setHasError(true);
      reset();
    } finally {
      setIsLoading(false);
    }
  };

  const save = async (patchIds: number[]): Promise<boolean> => {
    if (!companyId.value || patchIds.length === 0) return false;

    try {
      setIsSaving(true);
      setHasError(false);
      const storedFilters: TStoredFilters = getStoredFilters();
      const wrappedFilters: TWrappedFilters = wrapForSave(storedFilters);

      await managerCompanyModel.updateFilters(companyId.value, {
        patches: patchIds,
        sale_type: selectedType.value,
        filters: wrappedFilters,
      });

      // Update tracked state after successful save
      syncSaved(storedFilters, wrappedFilters);

      onStateSnapshot();

      return true;
    } catch {
      setHasError(true);

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    filters,
    isLoading,
    isSaving,
    hasError,
    load,
    save,
    reset,
    extractStoredFilters: getStoredFilters,
    applyStoredFilters,
  };
};
